import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  useIsMobile,
  useIsServerSide,
  useProductsByCategory,
  // useScreenWidth,
  useSelectedStore,
  useSpacingBetweenProductCard,
} from '@/hooks';
import { useAnalytics } from '@/data';
import {
  Container,
  Heading,
  Icon,
  Link,
  Placeholder,
  PrismicImageWithAlt,
  Product,
  ProductPlaceholder,
  Slider,
} from '@/components';
import {
  EnumCustomerType,
  GProduct,
  SortBySelectOption,
  TreezStore,
} from '@/types';

import style from './ProductSideBySide.module.scss';
import { alignContent, getSlicePadding, stripHtml } from 'utils/utils';
import { ColorField, ImageField } from '@prismicio/client';
import { TEMPLATE_BASE_PATH } from 'constants/routes';

interface IProductSideBySideProps {
  applyShadowToImage?: boolean;
  title?: string;
  titleColor?: ColorField;
  analyticsListName?: string;
  products: GProduct[];
  totalCount?: number;
  seeAllHref?: string;
  seeMoreText?: string;
  seeAllLinkColor?: ColorField;
  image?: ImageField<'mobile'>;
  imagePosition?: 'left' | 'right';
  imageHeigthOnMobile?: number;
  imageHeigthOnDesktop?: number;
  hideOnMobile?: boolean;
  backgroundSection?: ColorField;
  imageBackgroundColor?: ColorField;
  verticalPosition?: 'start' | 'center' | 'end' | undefined;
  horizontalPosition?: 'start' | 'center' | 'end' | undefined;
  showPagination?: boolean;
  paginationBackgroundColor?: ColorField;
  paginationProgressColor?: ColorField;
  selectedStore?: TreezStore;
  selectedCustomerType?: EnumCustomerType;

  // limit and sort
  limit?: number;
  sortBy?: SortBySelectOption | undefined;
  fullWidth?: boolean;
  filterByStore?: boolean;
  filterByCategory?: string; // filter by category
  filterByBrand?: string; // filter by brand
  filterByGroupId?: string; // filter by group id
  filterByOnSale?: boolean; // filter by on sale
  spacingBottom?: boolean;
  paddingSection?: 'small' | 'medium' | 'large';
}

const BREACKPOINT = 767;

const ProductSideBySide: FC<IProductSideBySideProps> = ({
  title,
  titleColor,
  analyticsListName,
  totalCount: _totalCount = 0,
  seeAllHref,
  products: serverProducts,
  seeMoreText = 'See All',
  seeAllLinkColor,
  image,
  imageHeigthOnMobile,
  imageHeigthOnDesktop,
  imagePosition = 'left',
  verticalPosition = 'start',
  horizontalPosition = 'start',
  backgroundSection,
  imageBackgroundColor,
  hideOnMobile,
  applyShadowToImage,
  showPagination,
  paginationBackgroundColor,
  paginationProgressColor,

  limit = 20,
  sortBy,
  fullWidth,

  filterByStore = false,
  filterByCategory,
  filterByBrand,
  filterByGroupId,
  filterByOnSale,
  paddingSection,
  spacingBottom,
  selectedStore,
  selectedCustomerType,
}) => {
  const { measureProductImpressions, measureViewItemsListGA4 } = useAnalytics();
  const { isMobile } = useIsMobile(BREACKPOINT);
  const { isServerSide } = useIsServerSide();
  const imageRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(
    `${image?.dimensions?.height || 550}px`,
  );

  const {
    breakpoints,
    defaultSlidePerView,
    defaultSpaciBetween,
    spacingClass,
    isProductCardLargImage,
  } = useSpacingBetweenProductCard();
  const { store } = useSelectedStore();

  useEffect(() => {
    measureProductImpressions(products, analyticsListName as string);
    measureViewItemsListGA4(products, analyticsListName as string);
  }, []);

  const { products, isLoading, total, isError, error } = useProductsByCategory({
    store: store ?? selectedStore,
    sortBy,
    limit,
    products: serverProducts,
    filterByStore,
    filterByBrand,
    filterByCategory,
    filterByGroupId,
    filterByOnSale,
    customerType: selectedCustomerType,
  });

  const getCustomHeight = () => {
    if (isMobile) {
      return imageHeigthOnMobile;
    }

    return imageHeigthOnDesktop;
  };

  const updateHeight = () => {
    let height = getCustomHeight();

    if (!height && imageRef.current) {
      height = imageRef.current.clientHeight;
    }

    setMaxHeight(`${height}px`);
  };

  useEffect(() => {
    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    if (imageRef.current) {
      resizeObserver.observe(imageRef.current);
    }

    window.addEventListener('resize', updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [isMobile, imageHeigthOnMobile, imageHeigthOnDesktop]);

  const showContentInfo = !(hideOnMobile && isMobile);

  if (isError) {
    // eslint-disable-next-line no-console
    console.log(error);

    return null;
  }

  if (!isLoading && !products?.length) {
    return null;
  }

  return (
    <section
      className={classNames(
        style.product__sidebyside,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: spacingBottom,
        },
      )}
      style={{ background: backgroundSection! }}
      //data-slice-type={slice.slice_type}
      //data-slice-variation={slice.variation}
    >
      <div className={style.product__sidebyside_body}>
        <Container
          className={classNames(style.product__sidebyside_container, {
            [style.product__sidebyside_container_invested]:
              imagePosition === 'right',
          })}
        >
          {showContentInfo && (
            <div
              className={classNames(style.product__sidebyside_info_content, {
                [style.product__sidebyside_info_content_not_fullwidth]:
                  !fullWidth,
              })}
              style={{
                alignItems: alignContent(verticalPosition ?? 'start'),
                height: maxHeight,
              }}
            >
              {!isServerSide && (
                <div
                  className={classNames(style.info, {
                    [style.info__full_width]: fullWidth,
                  })}
                  style={{
                    alignItems: alignContent(horizontalPosition ?? 'start'),
                  }}
                >
                  <h2
                    className={style.product__sidebyside_title}
                    dangerouslySetInnerHTML={{
                      __html: title!,
                    }}
                    style={{
                      color: titleColor!,
                    }}
                  />
                  <Link
                    aria-label="Next"
                    className={style.products__see_all}
                    href={seeAllHref ?? TEMPLATE_BASE_PATH.SHOP}
                    style={{ color: seeAllLinkColor! }}
                  >
                    {seeMoreText}
                    <Icon name="arrow-right" />
                  </Link>
                </div>
              )}
              <figure
                className={classNames(style.image_section, {
                  [style.image_section__full_width_left]:
                    imagePosition === 'left' && fullWidth,
                  [style.image_section__full_width_rigth]:
                    imagePosition === 'right' && fullWidth,
                  [style.image_section_border_radius]: !fullWidth,
                })}
                ref={imageRef}
                style={{
                  background: imageBackgroundColor!,
                }}
              >
                {isServerSide ? (
                  <Placeholder style={{ width: '100%', height: maxHeight }} />
                ) : (
                  <PrismicImageWithAlt
                    defaultAlt="Promotion image"
                    field={isMobile ? image?.mobile : image}
                    onLoad={updateHeight}
                    style={{
                      objectFit: getCustomHeight() ? 'cover' : 'contain',
                      width: '100%',
                      height: getCustomHeight() ? maxHeight : 'auto',
                    }}
                  />
                )}
              </figure>
              {applyShadowToImage && !isServerSide && (
                <span
                  className={classNames(style.overlay, {
                    [style.overlay__full_width_left]:
                      imagePosition === 'left' && fullWidth,
                    [style.overlay__full_width_rigth]:
                      imagePosition === 'right' && fullWidth,
                    [style.overlay__border_radius]: !fullWidth,
                  })}
                />
              )}
            </div>
          )}
          {!isMobile && (
            <div
              className={style.product__sidebyside_content_wrapper}
              style={{ maxHeight: maxHeight }}
            >
              <div
                className={classNames(
                  style.product__sidebyside_content_product,
                  spacingClass,
                  {
                    [style.product__sidebyside_content_product_large]:
                      isProductCardLargImage,
                  },
                )}
              >
                {isLoading
                  ? new Array(20)
                      .fill(null)
                      .map((_, index) => (
                        <ProductPlaceholder
                          key={index}
                          largeCard={isProductCardLargImage}
                        />
                      ))
                  : products?.map((prod, index) => (
                      <Product
                        className={style.content__product}
                        key={index}
                        product={prod}
                      />
                    ))}
              </div>
            </div>
          )}
          {isMobile && (
            <Slider
              activatedHover
              breakpoints={breakpoints}
              centeredSlides={false}
              className={classNames(style.products__carousel, {
                [style.products__carousel_pagination]: showPagination,
              })}
              freeMode
              isNotPaddingMobile={false}
              paginationBackground={paginationBackgroundColor}
              paginationProgressColor={paginationProgressColor}
              seeAllElement={
                hideOnMobile && (
                  <Link
                    aria-label={`Go to ${seeAllHref ?? '/deals'}`}
                    className={style.products__see_all}
                    href={seeAllHref ?? '/deals'}
                  >
                    {seeMoreText}
                    {total > 0 && <span>({total})</span>}
                  </Link>
                )
              }
              showPagination={showPagination}
              showSliderButtons={false}
              showSliderCardLink
              showSlidesOutsideContainer
              sliderHeading={
                hideOnMobile ? (
                  <Heading level={2}>{stripHtml(title)}</Heading>
                ) : undefined
              }
              slidesPerView={defaultSlidePerView}
              spaceBetween={defaultSpaciBetween}
            >
              {isLoading
                ? Array.from({ length: 20 }).map((_, index) => (
                    <ProductPlaceholder
                      key={index}
                      largeCard={isProductCardLargImage}
                    />
                  ))
                : products.map(product => (
                    <Product
                      analyticsListName={analyticsListName}
                      className={style.slide__main}
                      key={product.id}
                      product={product}
                    />
                  ))}
              <Link
                aria-label={`Go to ${seeAllHref ?? '/deals'}`}
                className={classNames(
                  style.slide__main,
                  style.product__shop_all_slider,
                )}
                href={seeAllHref ?? '/deals'}
              >
                <Icon name="right" />
                <p>{seeMoreText}</p>
              </Link>
            </Slider>
          )}
        </Container>
      </div>
    </section>
  );
};

export default ProductSideBySide;
